var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Persona, PersonaSize, Link, Stack, Text } from '@fluentui/react';
import ProfilePhotoService from '../../../services/ProfilePhoto.service';
import FxpSharedComponent from '../../shared/FxpSharedComponent';
/** The Staffing-owned component legacy mounts, and the app that publishes it. */
export const STAFFING_APP_NAME = 'staffingWebApplication';
export const RESOURCE_INFO_CARD_COMPONENT = 'resource-info-card';
export const LocalResourceInfoCard = ({ alias, name, role, size = PersonaSize.size72, showDetails = true, profileHref, photoService, }) => {
    const [photoUrl, setPhotoUrl] = useState('');
    useEffect(() => {
        let cancelled = false;
        let ownedUrl = '';
        setPhotoUrl('');
        if (!alias) {
            return undefined;
        }
        const service = photoService !== null && photoService !== void 0 ? photoService : ProfilePhotoService.getSingleton();
        service
            .getPhotoUrl(alias)
            .then((url) => {
            var _a;
            // The service may hand back an object URL it minted from a Blob; if this effect has
            // already been superseded we must release it here or it leaks for the page's lifetime.
            if (cancelled) {
                (_a = service.releasePhotoUrl) === null || _a === void 0 ? void 0 : _a.call(service, url);
                return;
            }
            ownedUrl = url;
            setPhotoUrl(url);
        })
            .catch(() => {
            // Decorative: Persona falls back to initials.
        });
        return () => {
            var _a;
            cancelled = true;
            (_a = service.releasePhotoUrl) === null || _a === void 0 ? void 0 : _a.call(service, ownedUrl);
        };
    }, [alias, photoService]);
    const displayName = name || alias;
    return (React.createElement(Stack, { horizontal: true, verticalAlign: "center", tokens: { childrenGap: 12 }, "data-testid": "resource-info-card" },
        React.createElement(Persona, { text: displayName, imageUrl: photoUrl || undefined, size: size, hidePersonaDetails: true, imageAlt: "", "aria-hidden": "true", "data-testid": "resource-info-card-avatar" }),
        showDetails ? (React.createElement(Stack, { tokens: { childrenGap: 2 }, styles: { root: { minWidth: 0 } } },
            React.createElement(Text, { variant: "mediumPlus", styles: { root: { fontWeight: 600 } }, title: displayName, "data-testid": "resource-info-card-name" }, displayName),
            profileHref ? (React.createElement(Link, { href: profileHref, target: "_blank", rel: "noopener noreferrer", title: `Open ${displayName}'s profile`, "data-testid": "resource-info-card-alias" }, alias)) : (React.createElement(Text, { variant: "small", "data-testid": "resource-info-card-alias" }, alias)),
            role ? (React.createElement(Text, { variant: "small", title: role, "data-testid": "resource-info-card-role" }, role)) : null)) : null));
};
/**
 * Resource identity for the DSE modal.
 *
 * Hosts Staffing's real `resource-info-card` through the FXP shared-component loader — the same
 * component legacy mounts at `dseModal.template.html:15-19`, with the inputs legacy builds at
 * `dseModal.controller.js:32-56`. That gives full fidelity including the Live Persona Card hover,
 * because Staffing's bundle bootstraps LPC with their own `clientType` and Loki token.
 *
 * `LocalResourceInfoCard` renders instead whenever the loader is unavailable, and can be forced
 * via `forceFallback` if Staffing's card regresses (their `<mgt-person>` avatar is on the Graph
 * Toolkit, which retires 2026-08-28).
 */
export const ResourceInfoCard = (props) => {
    const { alias, name, role, showDetails = true, avatarSize = 'large', showPresence = false, showRole, forceFallback, renderTimeoutMs, fallbackDelayMs, registry, } = props;
    const inputs = React.useMemo(() => {
        // Property names must match ResourceInfoCardComponent's @Input names exactly — the loader
        // assigns them straight onto the component instance.
        const built = {
            useralias: alias,
            showDetails,
            avatarOnly: !showDetails,
            avatarSize,
            username: name || alias,
            showPresence,
        };
        if (showRole !== undefined) {
            built.showRole = showRole;
        }
        if (role !== undefined) {
            built.role = role;
        }
        return built;
    }, [alias, name, role, showDetails, avatarSize, showPresence, showRole]);
    return (React.createElement(FxpSharedComponent, { appName: STAFFING_APP_NAME, componentName: RESOURCE_INFO_CARD_COMPONENT, inputs: inputs, forceFallback: forceFallback, renderTimeoutMs: renderTimeoutMs, fallbackDelayMs: fallbackDelayMs, registry: registry, 
        // Legacy gives the host `display: inline-block` (`fm-dashboard.scss:1673-1675`).
        style: { display: 'inline-block' }, fallback: React.createElement(LocalResourceInfoCard, Object.assign({}, props)) }));
};
export default ResourceInfoCard;
//# sourceMappingURL=ResourceInfoCard.js.map